import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Device } from '@capacitor/device';
import { StatusBar } from '@capacitor/status-bar';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { App } from '@capacitor/app';

// Define extended interfaces for torch functionality
interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
  torch?: boolean;
}

interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
  torch?: boolean;
  zoom?: number;
  focusMode?: string;
  focusDistance?: number;
  exposureMode?: string;
  pointsOfInterest?: Array<{x: number, y: number}>;
}

interface ContinuousCameraProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotosCaptured: (photoDataUrls: string[]) => void;
  maxPhotos?: number;
}

const ContinuousCamera: React.FC<ContinuousCameraProps> = ({
  isOpen,
  onClose,
  onPhotosCaptured,
  maxPhotos = 30
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [capturedPhotos, setCapturedPhotos] = useState<string[]>([]);
  const [photoCount, setPhotoCount] = useState(0);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isFrontCamera, setIsFrontCamera] = useState(false);
  const [isFlashOn, setIsFlashOn] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const [cameraVisible, setCameraVisible] = useState(false);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isPinching, setIsPinching] = useState(false);
  const touchStartDistanceRef = useRef<number>(0);
  const lastZoomRef = useRef<number>(1);
  const [focusIndicator, setFocusIndicator] = useState<{x: number, y: number, show: boolean}>({x: 0, y: 0, show: false});
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [zoomCapabilities, setZoomCapabilities] = useState<{min: number, max: number}>({min: 1, max: 4});
  const [supportsNativeZoom, setSupportsNativeZoom] = useState(false);

  const stopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setIsCameraReady(false);
  };

  // Memoize handleDone to avoid recreation on each render
  const handleDone = useCallback(() => {
    if (capturedPhotos.length > 0) {
      // First animate the camera out
      setCameraVisible(false);
      
      // Wait for animation to complete before actually closing
      setTimeout(() => {
        onPhotosCaptured(capturedPhotos);
        stopCamera();
        onClose();
      }, 300);
    } else {
      setCameraVisible(false);
      setTimeout(() => {
        stopCamera();
        onClose();
      }, 300);
    }
  }, [capturedPhotos, onPhotosCaptured, onClose]);

  // Handle back button on Android and Escape key on web
  useEffect(() => {
    const handleBackButton = () => {
      handleDone();
      return true; // Prevent default back button behavior
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleDone();
      }
    };

    // Check if running on a native platform
    const checkPlatform = async () => {
      try {
        const info = await Device.getInfo();
        const isNative = info.platform !== 'web';
        setIsNativePlatform(isNative);
        
        if (isNative) {
          // Add hardware back button listener for Android
          App.addListener('backButton', handleBackButton);
        }
      } catch (err) {
        console.log('Device API not available, assuming web platform');
      }
    };

    checkPlatform();
    
    // Add escape key listener for web
    window.addEventListener('keydown', handleEscKey);

    return () => {
      // Cleanup listeners
      window.removeEventListener('keydown', handleEscKey);
      
      if (isNativePlatform) {
        App.removeAllListeners();
      }
    };
  }, [handleDone, isNativePlatform]);

  // Effect to handle animations
  useEffect(() => {
    if (isOpen) {
      // Wait a tiny bit to trigger the animation
      setTimeout(() => setCameraVisible(true), 10);
    } else {
      setCameraVisible(false);
    }
  }, [isOpen]);

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      // Get current orientation
      const isPortrait = window.innerHeight > window.innerWidth;
      setOrientation(isPortrait ? 'portrait' : 'landscape');
    };

    // Set initial orientation
    handleOrientationChange();

    // Add event listener for orientation changes
    window.addEventListener('resize', handleOrientationChange);
    
    // Try to unlock orientation on mobile devices when camera opens
    const unlockOrientation = async () => {
      try {
        await ScreenOrientation.unlock();
        console.log('Screen orientation unlocked');
      } catch (err) {
        console.log('Screen orientation API not available or failed to unlock', err);
      }
    };
    
    if (isOpen) {
      unlockOrientation();
    }

    // Clean up event listener
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [isOpen]);

  useEffect(() => {
    const checkPlatform = async () => {
      const info = await Device.getInfo();
      setIsIOS(info.platform === 'ios');
    };
    checkPlatform();
  }, []);

  useEffect(() => {
    if (isOpen) {
      startCamera();
      
      // Lock body scroll when camera is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.top = '0';
      document.body.style.left = '0';
      
      // Set viewport meta to prevent scaling
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
      }
      
      // Hide status bar on iOS if possible
      try {
        StatusBar.hide();
      } catch (err) {
        console.log('Status bar API not available');
      }
    } else {
      stopCamera();
      
      // Restore body scroll when camera is closed
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.top = '';
      document.body.style.left = '';
      
      // Restore viewport meta
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 
          'width=device-width, initial-scale=1.0, viewport-fit=cover');
      }
      
      // Show status bar again
      try {
        StatusBar.show();
      } catch (err) {
        console.log('Status bar API not available');
      }
    }

    return () => {
      stopCamera();
      
      // Clean up body styles when unmounting
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.top = '';
      document.body.style.left = '';
      
      // Restore viewport meta
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 
          'width=device-width, initial-scale=1.0, viewport-fit=cover');
      }
      
      // Ensure status bar is shown when component unmounts
      try {
        StatusBar.show();
      } catch (err) {
        console.log('Status bar API not available');
      }
    };
  }, [isOpen, isFrontCamera]);

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this browser');
      }

      const constraints = {
        video: {
          facingMode: isFrontCamera ? 'user' : 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Check capabilities
      const tracks = stream.getVideoTracks();
      let torchAvailable = false;
      
      for (const track of tracks) {
        const capabilities = track.getCapabilities() as any;
        
        // Check zoom capabilities
        if (capabilities.zoom) {
          const minZoom = capabilities.zoom.min || 0.5;
          const maxZoom = Math.min(capabilities.zoom.max || 4, 25); // Cap at 25x
          setZoomCapabilities({ min: minZoom, max: maxZoom });
          setSupportsNativeZoom(true);
          console.log('Camera zoom capabilities:', { min: minZoom, max: maxZoom });
        } else {
          console.log('Native zoom not supported, using CSS fallback');
          setSupportsNativeZoom(false);
        }
        
        // Check torch
        if (capabilities.torch) {
          torchAvailable = true;
          setHasTorch(true);
          
          // Apply flash if needed
          if (isFlashOn) {
            try {
              await track.applyConstraints({
                advanced: [{ torch: true } as ExtendedMediaTrackConstraintSet]
              });
            } catch (err) {
              console.error('Error setting torch:', err);
            }
          }
        }
      }
      
      // Reset flash state if not available
      if (!torchAvailable && isFlashOn) {
        setIsFlashOn(false);
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        mediaStreamRef.current = stream;
        setIsCameraReady(true);
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      alert('Could not start camera. Please check permissions.');
      onClose();
    }
  };

  const capturePhoto = () => {
    if (!isCameraReady || !videoRef.current || !canvasRef.current || isCapturing) return;

    try {
      // Set capturing state to prevent multiple rapid captures
      setIsCapturing(true);
      
      // Visual feedback - screen flash effect
      const flashElement = document.createElement('div');
      flashElement.className = 'camera-flash';
      flashElement.style.position = 'fixed';
      flashElement.style.top = '0';
      flashElement.style.left = '0';
      flashElement.style.right = '0';
      flashElement.style.bottom = '0';
      flashElement.style.backgroundColor = 'white';
      flashElement.style.opacity = '0.7';
      flashElement.style.zIndex = '10000';
      flashElement.style.pointerEvents = 'none';
      document.body.appendChild(flashElement);
      
      // Fade out the flash element
      setTimeout(() => {
        flashElement.style.transition = 'opacity 300ms ease-out';
        flashElement.style.opacity = '0';
        setTimeout(() => {
          document.body.removeChild(flashElement);
        }, 300);
      }, 50);

      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // High quality: Calculate dimensions (max 1600x1200 maintaining aspect ratio)
      const maxWidth = 1600;
      const maxHeight = 1200;
      let width = video.videoWidth;
      let height = video.videoHeight;
      
      if (width > maxWidth || height > maxHeight) {
        const widthRatio = maxWidth / width;
        const heightRatio = maxHeight / height;
        const ratio = Math.min(widthRatio, heightRatio);
        
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      
      // Set canvas dimensions to optimized size
      canvas.width = width;
      canvas.height = height;
      
      // Draw the current video frame to the canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      // Use better image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(video, 0, 0, width, height);
      
      // Convert canvas to data URL with high quality compression (90% quality)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.90);
      
      // Add to captured photos
      setCapturedPhotos(prev => [...prev, dataUrl]);
      
      // Update count with visual feedback
      setPhotoCount(count => {
        // Flash the counter in green
        const counter = document.querySelector('.photo-counter');
        if (counter) {
          counter.classList.add('photo-count-flash');
          setTimeout(() => {
            counter.classList.remove('photo-count-flash');
          }, 300);
        }
        return count + 1;
      });
      
      // Provide haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      // Play shutter sound on iOS (if supported)
      if (isIOS) {
        playShutterSound();
      }
      
      // Reset capturing state after a short delay to prevent rapid-fire captures
      setTimeout(() => {
        setIsCapturing(false);
      }, 300);
    } catch (error) {
      console.error('Error capturing photo:', error);
      setIsCapturing(false);
    }
  };

  const playShutterSound = () => {
    try {
      // Simple shutter sound - could be replaced with an actual audio file
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      // Ignore errors with audio
    }
  };

  const switchCamera = () => {
    setIsFrontCamera(prev => !prev);
  };

  const toggleFlash = () => {
    // Only toggle if camera is ready and torch is available
    if (isCameraReady && mediaStreamRef.current && hasTorch) {
      const newState = !isFlashOn;
      setIsFlashOn(newState);
      
      // Apply to current track
      const tracks = mediaStreamRef.current.getVideoTracks();
      for (const track of tracks) {
        const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities;
        if (capabilities.torch) {
          track.applyConstraints({
            advanced: [{ torch: newState } as ExtendedMediaTrackConstraintSet]
          }).catch(err => {
            console.error('Error toggling flash:', err);
            setIsFlashOn(false);
          });
        }
      }
    }
  };

  // Pinch to zoom handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      setIsPinching(true);
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      touchStartDistanceRef.current = distance;
      lastZoomRef.current = zoom;
    } else if (e.touches.length === 1 && !isPinching) {
      // Single tap - handle focus
      handleTapToFocus(e);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && isPinching) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      const scale = distance / touchStartDistanceRef.current;
      let newZoom = Math.min(Math.max(lastZoomRef.current * scale, zoomCapabilities.min), zoomCapabilities.max);
      
      // Discrete zoom snapping between 0.5x and 1x
      const hasUltraWide = zoomCapabilities.min <= 0.5;
      
      if (hasUltraWide) {
        // Zooming in from 0.5x: snap to 1x when crossing 0.75x threshold
        if (lastZoomRef.current <= 0.5 && newZoom > 0.75) {
          newZoom = 1;
        }
        // Zooming out from 1x: snap to 0.5x when crossing 0.75x threshold
        else if (lastZoomRef.current >= 1 && newZoom < 0.75) {
          newZoom = 0.5;
        }
        // Keep at 0.5x if trying to zoom below it
        else if (newZoom > 0.5 && newZoom < 1) {
          // Don't allow intermediate values - stay at current discrete level
          newZoom = lastZoomRef.current;
        }
      }
      
      setZoom(newZoom);
      
      // Don't apply camera constraints during gesture - causes freezing
      // Apply only when gesture ends for instant zoom
    }
  };

  const handleTouchEnd = () => {
    if (isPinching) {
      setIsPinching(false);
      
      // Only apply camera constraints for optical zoom (≤1x)
      // For digital zoom (>1x), CSS transform is sufficient
      if (supportsNativeZoom && zoom <= 1) {
        applyZoomToTrack(zoom);
      }
    }
  };

  // Apply zoom to camera track
  const applyZoomToTrack = (zoomLevel: number) => {
    if (!mediaStreamRef.current) return;
    
    const tracks = mediaStreamRef.current.getVideoTracks();
    for (const track of tracks) {
      const capabilities = track.getCapabilities() as any;
      if (capabilities.zoom) {
        track.applyConstraints({
          advanced: [{ zoom: zoomLevel } as ExtendedMediaTrackConstraintSet]
        }).catch(err => {
          console.log('Failed to apply zoom:', err);
        });
      }
    }
  };

  // Quick zoom handler
  const handleQuickZoom = (targetZoom: number) => {
    // Only allow zoom levels within device capabilities
    if (targetZoom < zoomCapabilities.min || targetZoom > zoomCapabilities.max) {
      return;
    }
    
    setZoom(targetZoom);
    lastZoomRef.current = targetZoom;
    
    // Only apply camera constraints for optical zoom (≤1x)
    // For digital zoom (>1x), use CSS only
    if (supportsNativeZoom && targetZoom <= 1) {
      applyZoomToTrack(targetZoom);
    }
  };

  // Reset zoom to 1x
  const handleResetZoom = () => {
    if (zoom === 1) return;
    handleQuickZoom(1);
  };

  // Tap to focus handler
  const handleTapToFocus = async (e: React.TouchEvent) => {
    if (!videoContainerRef.current || !mediaStreamRef.current) return;
    
    const touch = e.touches[0];
    const rect = videoContainerRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    // Show focus indicator
    setFocusIndicator({ x, y, show: true });
    
    // Calculate normalized coordinates (0-1)
    const normalizedX = x / rect.width;
    const normalizedY = y / rect.height;
    
    // Apply focus/exposure point to camera
    const tracks = mediaStreamRef.current.getVideoTracks();
    for (const track of tracks) {
      const capabilities = track.getCapabilities() as any;
      const constraints: any = { advanced: [] };
      
      // Try multiple strategies for focus
      
      // Strategy 1: Manual focus with focus distance
      if (capabilities.focusMode && capabilities.focusDistance) {
        try {
          await track.applyConstraints({
            advanced: [{ focusMode: 'manual' } as ExtendedMediaTrackConstraintSet]
          });
          // Calculate focus distance based on tap position (closer to center = further focus)
          const centerX = 0.5;
          const centerY = 0.5;
          const distanceFromCenter = Math.hypot(normalizedX - centerX, normalizedY - centerY);
          const focusDistance = Math.max(0.1, Math.min(1, 0.5 - distanceFromCenter));
          
          await track.applyConstraints({
            advanced: [{ focusDistance } as ExtendedMediaTrackConstraintSet]
          });
          console.log('Applied manual focus:', focusDistance);
        } catch (err) {
          console.log('Manual focus not supported:', err);
        }
      }
      
      // Strategy 2: Exposure point
      if (capabilities.exposureMode) {
        try {
          await track.applyConstraints({
            advanced: [{
              exposureMode: 'continuous',
              pointsOfInterest: [{ x: normalizedX, y: normalizedY }]
            } as ExtendedMediaTrackConstraintSet]
          });
          console.log('Applied exposure point');
        } catch (err) {
          console.log('Exposure point not supported:', err);
        }
      }
      
      // Strategy 3: Continuous focus with points of interest
      if (capabilities.focusMode) {
        try {
          await track.applyConstraints({
            advanced: [{
              focusMode: 'continuous',
              pointsOfInterest: [{ x: normalizedX, y: normalizedY }]
            } as ExtendedMediaTrackConstraintSet]
          });
          console.log('Applied continuous focus with POI');
        } catch (err) {
          console.log('Continuous focus with POI not supported:', err);
        }
      }
    }
    
    // Hide focus indicator after animation
    setTimeout(() => {
      setFocusIndicator(prev => ({ ...prev, show: false }));
    }, 1000);
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-[999999] m-0 p-0 bg-black flex flex-col transition-opacity duration-300 ease-in-out ${cameraVisible ? 'opacity-100' : 'opacity-0'}`} 
      style={{
        height: '100vh', 
        width: '100vw',
        touchAction: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        isolation: 'isolate'
      }}
      id="continuous-camera-root"
    >
      {/* Camera View */}
      <div 
        ref={videoContainerRef}
        className={`relative flex-1 overflow-hidden w-full h-full transition-transform duration-300 ease-in-out ${cameraVisible ? 'scale-100' : 'scale-110'}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <video 
          ref={videoRef} 
          className={`absolute inset-0 h-full w-full object-cover m-0 p-0 ${orientation === 'landscape' ? 'landscape-video' : ''}`}
          style={{ 
            // Only use CSS scale for zoom > 1 (digital zoom)
            // For zoom <= 1 (including 0.5x ultra-wide), use native camera zoom only
            transform: zoom > 1 ? `scale(${zoom})` : 'scale(1)',
            // No transition for ultra-wide (<=1x) - instant camera zoom
            // Transition only for digital zoom (>1x)
            transition: isPinching || zoom <= 1 ? 'none' : 'transform 0.3s ease-out',
            willChange: 'transform'
          }}
          autoPlay 
          playsInline 
          muted
        />
        
        {/* Canvas for capturing photos - hidden */}
        <canvas ref={canvasRef} className="hidden" />
        
        {/* Photo counter - adjusted for safe area */}
        <div className={`absolute top-10 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500/90 to-indigo-600/90 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-bold z-10 photo-counter transition-all duration-300 shadow-lg ${cameraVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
            <span>{photoCount} {photoCount === 1 ? 'Photo' : 'Photos'}</span>
          </div>
        </div>
        
        {/* Zoom Controls */}
        {isCameraReady && (
          <div className="absolute top-24 left-1/2 transform -translate-x-1/2 flex items-center gap-2 z-10">
            {/* 0.5x Button (if available) */}
            {zoomCapabilities.min <= 0.5 && (
              <button
                onClick={() => handleQuickZoom(0.5)}
                className={`px-3 py-1.5 rounded-full font-semibold text-xs transition-all shadow-lg ${
                  Math.abs(zoom - 0.5) < 0.1
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white scale-105'
                    : 'bg-gradient-to-r from-slate-700/90 to-slate-800/90 backdrop-blur-sm text-white hover:from-slate-600/90 hover:to-slate-700/90 hover:scale-105'
                } active:scale-95`}
              >
                .5
              </button>
            )}
            
            {/* Current Zoom Display (clickable to reset to 1x) */}
            <button
              onClick={handleResetZoom}
              className={`px-4 py-2 rounded-full font-bold text-sm transition-all shadow-lg ${
                Math.abs(zoom - 1) < 0.1
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white cursor-default'
                  : 'bg-gradient-to-r from-slate-700/90 to-slate-800/90 backdrop-blur-sm text-white hover:from-blue-500/90 hover:to-indigo-600/90 hover:scale-105'
              } active:scale-95`}
            >
              <div className="flex items-center gap-1.5">
                <span>{zoom.toFixed(1)}x</span>
                {supportsNativeZoom && zoom < 1 && (
                  <span className="text-xs text-blue-200">W</span>
                )}
              </div>
            </button>
            
            {/* 2x Button (if available) */}
            {zoomCapabilities.max >= 2 && (
              <button
                onClick={() => handleQuickZoom(2)}
                className={`px-3 py-1.5 rounded-full font-semibold text-xs transition-all shadow-lg ${
                  Math.abs(zoom - 2) < 0.1
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white scale-105'
                    : 'bg-gradient-to-r from-slate-700/90 to-slate-800/90 backdrop-blur-sm text-white hover:from-slate-600/90 hover:to-slate-700/90 hover:scale-105'
                } active:scale-95`}
              >
                2
              </button>
            )}
          </div>
        )}
        
        {/* Focus indicator */}
        {focusIndicator.show && (
          <div 
            className="absolute pointer-events-none z-20 focus-indicator"
            style={{
              left: focusIndicator.x,
              top: focusIndicator.y,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="w-20 h-20 border-2 border-yellow-400 rounded-full animate-focus-pulse" />
          </div>
        )}
        
        {/* Controls Overlay - positioned based on orientation */}
        <div 
          className={`absolute inset-x-0 ${orientation === 'portrait' ? 'bottom-0' : 'bottom-0 right-0'} flex flex-col items-center space-y-6 pb-8 transition-all duration-300 ${cameraVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} 
          style={{
            paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
            paddingRight: orientation === 'landscape' ? 'max(env(safe-area-inset-right), 16px)' : undefined
          }}
        >
          {/* Capture Button */}
          <button
            onClick={capturePhoto}
            disabled={!isCameraReady || photoCount >= maxPhotos || isCapturing}
            className={`group w-24 h-24 rounded-full flex items-center justify-center transition-all duration-200 shadow-2xl ${
              !isCameraReady || isCapturing
                ? 'bg-gray-500/50 opacity-50' 
                : 'bg-white hover:scale-105 active:scale-95'
            }`}
            aria-label="Take photo"
          >
            <div className={`w-20 h-20 rounded-full border-4 transition-all ${
              !isCameraReady ? 'border-gray-400' : 
              isCapturing ? 'border-green-500 bg-green-500/20' : 
              'border-blue-500 group-hover:border-blue-600 group-hover:scale-95'
            }`} />
          </button>
          
          {/* Action Buttons */}
          <div className={`flex ${orientation === 'portrait' ? 'items-center justify-between w-full px-6' : 'flex-col items-end space-y-4'} pb-2`}>
            {/* Switch Camera */}
            <button
              onClick={switchCamera}
              disabled={!isCameraReady}
              className="w-14 h-14 rounded-full bg-gradient-to-br from-slate-700/80 to-slate-800/80 backdrop-blur-sm flex items-center justify-center text-white shadow-lg hover:from-slate-600/80 hover:to-slate-700/80 active:scale-95 transition-all disabled:opacity-50"
              aria-label="Switch camera"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12s2.545-5 7-5c4.454 0 7 5 7 5s-2.546 5-7 5c-4.455 0-7-5-7-5z"/>
                <path d="M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                <path d="M21 17v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2"/>
                <path d="M21 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2"/>
              </svg>
            </button>
            
            {/* Done Button */}
            <button
              onClick={handleDone}
              className={`${orientation === 'portrait' ? 'px-8 py-3.5' : 'w-14 h-14'} rounded-full ${
                capturedPhotos.length > 0 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-xl' 
                  : 'bg-gradient-to-r from-slate-700/80 to-slate-800/80 text-slate-300 backdrop-blur-sm'
              } font-bold shadow-lg flex items-center justify-center gap-2 transition-all hover:scale-105 active:scale-95`}
              aria-label="Done"
            >
              {orientation === 'portrait' ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span>Done ({photoCount})</span>
                </>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </button>
            
            {/* Flash Toggle */}
            <button
              onClick={toggleFlash}
              disabled={!isCameraReady || !hasTorch}
              className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95 ${
                isFlashOn 
                  ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white shadow-yellow-500/50' 
                  : 'bg-gradient-to-br from-slate-700/80 to-slate-800/80 backdrop-blur-sm text-white hover:from-slate-600/80 hover:to-slate-700/80'
              } ${!hasTorch ? 'opacity-50' : ''}`}
              aria-label={isFlashOn ? "Turn flash off" : "Turn flash on"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={isFlashOn ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <style>{`
        .photo-count-flash {
          background: linear-gradient(to right, rgb(16, 185, 129), rgb(5, 150, 105)) !important;
          transform: scale(1.1);
          box-shadow: 0 0 20px rgba(16, 185, 129, 0.5);
        }
        
        @keyframes focus-pulse {
          0% {
            transform: scale(1.2);
            opacity: 1;
          }
          50% {
            transform: scale(1);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.2);
            opacity: 0;
          }
        }
        
        .animate-focus-pulse {
          animation: focus-pulse 1s ease-out forwards;
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        
        @supports (padding: env(safe-area-inset-top)) {
          .safe-area-top {
            padding-top: env(safe-area-inset-top);
          }
          .safe-area-bottom {
            padding-bottom: env(safe-area-inset-bottom);
          }
        }
        
        /* Adjust video in landscape mode if needed */
        .landscape-video {
          object-fit: contain;
          background-color: black;
        }
      `}</style>
    </div>
  );
};

export default ContinuousCamera; 